import { useCallback, useEffect, useRef, useState } from 'react';
import { useOrgChart } from '../components/org-chart/useOrgChart';
import { useEditKeyboard } from '../components/org-chart/useEditKeyboard';
import { useModal, ModalProvider } from '../components/org-chart/useModal';
import OrgChartCanvas from '../components/org-chart/components/OrgChartCanvas';
import DrillDownPanel from '../components/org-chart/components/DrillDownPanel';
import OrgChartToolbar from '../components/org-chart/components/OrgChartToolbar';
import SelectionStatusBar, { EditNote } from '../components/org-chart/components/SelectionStatusBar';
import PersonEditor from '../components/org-chart/editors/PersonEditor';
import { ProgramEditor } from '../components/org-chart/editors/ProgramEditor';
import GroupEditor from '../components/org-chart/editors/GroupEditor';
import LeadershipEditor from '../components/org-chart/editors/LeadershipEditor';
import { ChartSettingsEditor } from '../components/org-chart/editors/ChartSettingsEditor';
import type { OrgChartDoc, Selection } from '../components/org-chart/types';

function computeStats(doc: OrgChartDoc) {
  let filled = 2, vacant = 0;
  doc.groups.forEach(gr => gr.programs.forEach(pr => pr.people.forEach(p => {
    if (p.vacant) vacant++; else filled++;
  })));
  return { filled, vacant, total: filled + vacant };
}

function OrgChartPageInner({ active = true }: { active?: boolean } = {}) {
  const {
    doc, layout, loading, loadError, retryLoad,
    selection, editMode, zoom, dispatch,
    canUndo, saveStatus, conflict, resolveConflictReload, resolveConflictOverwrite,
  } = useOrgChart();
  const { openModal, closeModal, confirm } = useModal();

  const svgWrapRef = useRef<SVGSVGElement | null>(null);
  const baselineRef = useRef<OrgChartDoc | null>(null);
  if (doc && baselineRef.current == null) baselineRef.current = doc;

  const [showSavedAck, setShowSavedAck] = useState(false);
  const prevEditModeRef = useRef(editMode);
  useEffect(() => {
    if (editMode) setShowSavedAck(false);
    else if (prevEditModeRef.current) setShowSavedAck(true);
    prevEditModeRef.current = editMode;
  }, [editMode]);
  useEffect(() => {
    if (!showSavedAck || saveStatus !== 'saved') return undefined;
    const t = setTimeout(() => setShowSavedAck(false), 5000);
    return () => clearTimeout(t);
  }, [showSavedAck, saveStatus]);

  const [editing, setEditing] = useState<{ kind: 'person' | 'prog'; gi: number; pi: number | null; ki?: number; isNew?: boolean } | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const handleClose = useCallback(() => dispatch({ type: 'DESELECT' }), [dispatch]);
  const handleSelect = useCallback((s: Selection) => dispatch({ type: 'SELECT', selection: s }), [dispatch]);
  const closeEditing = useCallback(() => setEditing(null), []);

  const handleAddPerson = useCallback((sel: Selection) => {
    const gi = sel && sel.kind !== 'sun' && 'gi' in sel ? sel.gi : 0;
    const pi = (sel && sel.kind === 'prog' ? sel.pi : 0) || 0;
    setEditing({ kind: 'person', gi, pi, isNew: true });
  }, []);
  const handleEditPerson = useCallback((sel: Selection) => {
    if (sel && sel.kind === 'person') setEditing({ kind: 'person', gi: sel.gi, pi: sel.pi, ki: sel.ki, isNew: false });
  }, []);
  const handleAddProgram = useCallback((gi: number) => { setEditing({ kind: 'prog', gi, pi: null }); }, []);
  const handleEditProgram = useCallback((sel: Selection) => {
    if (sel && sel.kind === 'prog') setEditing({ kind: 'prog', gi: sel.gi, pi: sel.pi });
  }, []);

  const openGroupEditor = useCallback((gi: number | null) => {
    openModal({
      id: `group-editor-${gi ?? 'new'}`,
      pageScope: 'page-org',
      title: gi == null ? 'Add group' : 'Edit group',
      render: () => <GroupEditor doc={doc!} layout={layout} gi={gi} dispatch={dispatch} closeModal={closeModal} confirm={confirm} />,
    });
  }, [doc, layout, dispatch, openModal, closeModal, confirm]);

  const openLeadershipEditor = useCallback(() => {
    openModal({
      id: 'leadership-editor',
      pageScope: 'page-org',
      title: 'Program leadership',
      render: () => <LeadershipEditor doc={doc!} dispatch={dispatch} closeModal={closeModal} />,
    });
  }, [doc, dispatch, openModal, closeModal]);

  const editSelected = useCallback((sel: Selection) => {
    if (!sel) return;
    if (sel.kind === 'person') handleEditPerson(sel);
    else if (sel.kind === 'prog') handleEditProgram(sel);
    else if (sel.kind === 'group') openGroupEditor(sel.gi);
    else openLeadershipEditor();
  }, [handleEditPerson, handleEditProgram, openGroupEditor, openLeadershipEditor]);

  useEditKeyboard({
    dispatch, selection, editMode, layout,
    sunX: doc ? doc.sun.x : 0, sunY: doc ? doc.sun.y : 0, sunR: doc ? doc.settings.sunR : 0,
    onActivate: editSelected,
  });

  useEffect(() => {
    if (!doc || !active) return;
    document.title = `${doc.settings.title} — Orbit`;
  }, [doc?.settings.title, active]);

  if (loading) {
    return <div id="page-org"><div className="org-load" role="status">Loading the org chart…</div></div>;
  }
  if (loadError || !doc) {
    return (
      <div id="page-org">
        <div className="org-load-error" role="alert">
          <p>{loadError || 'Could not load the org chart.'}</p>
          <button type="button" className="tb" onClick={retryLoad}>Retry</button>
        </div>
      </div>
    );
  }

  const stats = computeStats(doc);

  return (
    <div id="page-org">
      <div className="strip">
        <div><b>{stats.filled}</b><span>Filled FTE</span></div>
        <div className="v"><b>{stats.vacant}</b><span>Vacant</span></div>
        <div><b>{stats.total}</b><span>Total Auth.</span></div>
      </div>
      <div className="legend">
        <span className="lg c1">Program leadership</span>
        <span className="lg c2">Group / function</span>
        <span className="lg c3">Program</span>
        <span className="lg">Member</span>
        <span className="lg cv">Vacant seat</span>
      </div>
      <p className="hint">Select anyone on a rim — or any disc — for <b>contact details, reporting line &amp; team</b>.</p>

      <div className="toolbar">
        <OrgChartToolbar
          doc={doc} selection={selection} editMode={editMode} zoom={zoom} canUndo={canUndo}
          svgRef={svgWrapRef} dispatch={dispatch} baseline={baselineRef.current!}
          onAddPerson={handleAddPerson} onAddProgram={handleAddProgram}
          onAddGroup={() => openGroupEditor(null)} onEditPerson={handleEditPerson}
          onEditProgram={handleEditProgram}
          onEditGroup={(sel) => { if (sel && sel.kind === 'group') openGroupEditor(sel.gi); }}
          onEditLeadership={openLeadershipEditor} onSettings={() => setShowSettings(true)}
        />
        <SelectionStatusBar doc={doc} selection={selection} editMode={editMode} />
      </div>
      <EditNote editMode={editMode} />

      {(saveStatus === 'saving' || saveStatus === 'conflict' || (showSavedAck && saveStatus === 'saved')) && (
        <div className={`savebar savebar-${saveStatus}`} role="status" aria-live="polite">
          {saveStatus === 'saved' && <span className="save-msg save-ok">✓ All changes saved</span>}
          {saveStatus === 'saving' && <span className="save-msg">Saving…</span>}
          {saveStatus === 'conflict' && conflict && (
            <span className="save-msg save_alert" role="alert">
              ⛔ Someone else saved changes to this chart before you.
              <button type="button" className="tb" onClick={resolveConflictOverwrite}>Keep my changes</button>
              <button type="button" className="tb" onClick={resolveConflictReload}>Use their changes</button>
            </span>
          )}
        </div>
      )}

      <div className="wrap">
        <OrgChartCanvas ref={svgWrapRef} doc={doc} layout={layout} selection={selection} editMode={editMode} onSelect={handleSelect} dispatch={dispatch} onActivate={editSelected} />
      </div>

      <DrillDownPanel doc={doc} selection={editMode ? null : selection} onClose={handleClose} onSelect={handleSelect} />

      {editing?.kind === 'person' && (
        <PersonEditor key={`person:${editing.gi}:${editing.pi}:${editing.isNew ? 'new' : editing.ki}`} doc={doc} dispatch={dispatch} gi={editing.gi} pi={editing.pi as number} ki={editing.ki} isNew={editing.isNew} onClose={closeEditing} />
      )}
      {editing?.kind === 'prog' && (
        <ProgramEditor key={`prog:${editing.gi}:${editing.pi == null ? 'new' : editing.pi}`} doc={doc} dispatch={dispatch} gi={editing.gi} pi={editing.pi} onClose={closeEditing} />
      )}
      {showSettings && <ChartSettingsEditor settings={doc.settings} dispatch={dispatch} onClose={() => setShowSettings(false)} />}
    </div>
  );
}

export function OrgChartPage(props: { active?: boolean }) {
  return (
    <ModalProvider>
      <OrgChartPageInner {...props} />
    </ModalProvider>
  );
}

export default OrgChartPage;
